import { Injectable, Injector } from '@angular/core';
import {
    HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse
} from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

import { TokenStore } from '../../services/token-store';
import { SKIP_GLOBAL_SPINNER } from './spinner-context';
import { SKIP_AUTH } from './auth-context';
import { AuthSessionService } from '../../services/auth-session.service';

@Injectable()
export class AppInterceptor implements HttpInterceptor {
    /**
     * AuthSessionService is resolved lazily through the Injector, not injected
     * directly: it depends on HttpClient, and HttpClient depends on the
     * interceptor chain, so constructor injection would be a circular
     * dependency and Angular would fail to bootstrap.
     */
    constructor(private spinner: NgxSpinnerService, private injector: Injector) {}

    private get auth(): AuthSessionService {
        return this.injector.get(AuthSessionService);
    }

    getCookie(name: string): string | null {
        // POD1-10: guarded for SSR. This app renders through Angular Universal
        // (server.ts), where `document` does not exist - an unguarded read
        // here throws on every server-rendered request, and because it sits at
        // the top of intercept() it would take down every HTTP call the server
        // makes while rendering, not just the auth ones.
        if (typeof document === 'undefined' || !document.cookie) {
            return null;
        }
        const matches = document.cookie.match(new RegExp(
            '(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'
        ));
        return matches ? decodeURIComponent(matches[1]) : null;
    }

    /** Auth endpoints need the HttpOnly refresh cookie attached. */
    private isAuthEndpoint(url: string): boolean {
        return url.indexOf('/api/auth/') !== -1;
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // SCRUM-147: a request with its own page-level loader skips the
        // global full-page spinner (only one spinner on screen).
        const useSpinner = !req.context.get(SKIP_GLOBAL_SPINNER);
        const spinner = {
            show: () => { if (useSpinner) { this.spinner.show(); } },
            hide: () => { if (useSpinner) { this.spinner.hide(); } },
        };
        spinner.show();

        let headers = req.headers;

        // Production incident fix: every request now carries credentials
        // unconditionally. This used to default to req.withCredentials
        // (effectively false) and only flip to true for CSRF-bearing calls
        // or for URLs under /api/auth/ - see isAuthEndpoint() below. That
        // list missed /api-token-auth/, the LOGIN endpoint, whose response
        // is the FIRST thing that tries to set the sale4all_refresh cookie.
        // On a browser with no pre-existing cookie, an uncredentialed
        // request means the browser silently discards that Set-Cookie (this
        // is standard fetch/XHR behaviour for a cross-origin call, and
        // sale4all.co.il -> api.sale4all.co.il is cross-origin even though
        // it is same-site). The user looked logged in - the access token was
        // in memory - right up until it expired and the interceptor's silent
        // refresh below had no cookie to send, failed outright, and the user
        // was bounced. That is the "logged out ~1 minute after login" bug.
        // Our CORS config already allows credentials from the app's own
        // origins (CORS_ALLOW_CREDENTIALS + explicit CORS_ALLOWED_ORIGINS,
        // never '*'), so doing this for every request is safe.
        let withCredentials = true;

        // CSRF for cookie-bearing, state-changing requests.
        const csrfToken = this.getCookie('csrftoken');
        if (csrfToken && ['GET', 'HEAD'].indexOf(req.method) === -1) {
            headers = headers.set('X-CSRFToken', csrfToken);
        }

        // POD1-8: attach the in-memory access token, unless the caller already
        // set its own Authorization header (many services still build one via
        // ApiUrl.getJWTToken(), and overwriting it here would be surprising).
        // Login / signup calls never carry a session (see auth-context.ts).
        const skipAuth = req.context.get(SKIP_AUTH);
        if (skipAuth) {
            headers = headers.delete('Authorization');
        }
        const access = TokenStore.get();
        if (access && !skipAuth && !headers.has('Authorization')) {
            headers = headers.set('Authorization', 'Bearer ' + access);
        }

        // isAuthEndpoint() is still used below to stop a failing refresh
        // from recursing into another refresh - withCredentials no longer
        // depends on it.

        const modifiedReq = req.clone({ headers, withCredentials });

        return next.handle(modifiedReq).pipe(
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    spinner.hide();
                }
                return event;
            }),
            catchError((error: HttpErrorResponse) => {
                // A 401 on an ordinary call usually just means the 15-minute
                // access token expired. Refresh once and replay, so the user
                // never sees it.
                //
                // Never do this for /api/auth/ itself: a failing refresh would
                // recurse into another refresh.
                if (
                    error.status === 401 &&
                    !skipAuth &&
                    !this.isAuthEndpoint(req.url) &&
                    TokenStore.isLoggedIn()
                ) {
                    return this.auth.refresh().pipe(
                        switchMap((newAccess: string | null) => {
                            if (!newAccess) {
                                spinner.hide();
                                return throwError(error);
                            }
                            const retried = req.clone({
                                headers: headers.set(
                                    'Authorization', 'Bearer ' + newAccess),
                                withCredentials
                            });
                            return next.handle(retried).pipe(
                                map((event: HttpEvent<any>) => {
                                    if (event instanceof HttpResponse) {
                                        spinner.hide();
                                    }
                                    return event;
                                })
                            );
                        }),
                        catchError(refreshError => {
                            // Includes TOKEN_REUSE_DETECTED - the session was
                            // revoked for security reasons and is not
                            // retryable.
                            spinner.hide();
                            TokenStore.clear();
                            return throwError(refreshError);
                        })
                    );
                }

                spinner.hide();
                console.error(`Request failed: ${req.urlWithParams}`, error);
                return throwError(error);
            })
        );
    }
}
